import { Injectable } from "@angular/core";
import { Device } from "@capacitor/device";
import { FirebaseRemoteConfig } from "@capacitor-firebase/remote-config";
import packageInfo from "package.json";

export interface VersionConfig {
	minimumVersion: { ios: string; android: string };
	latestVersion: { ios: string; android: string };
	forceUpdate: boolean;
	updateMessage: string;
	storeUrls: { ios: string; android: string };
}

export interface VersionCheckResult {
	updateAvailable: boolean;
	forceUpdate: boolean;
	currentVersion: string;
	latestVersion: string;
	updateMessage: string;
	storeUrl: string;
}

@Injectable({
	providedIn: "root"
})
export class VersionCheckService {
	private initialized = false;

	constructor() {}

	/**
	 * Initialize Firebase Remote Config
	 * Call this once when app starts
	 */
	async initialize(): Promise<void> {
		if (this.initialized) return;

		try {
			const deviceInfo = await Device.getInfo();

			// Only initialize for native platforms
			if (deviceInfo.platform === "web") {
				console.log("VersionCheckService: Skipping on web platform");
				return;
			}

			// Fetch and activate with minimum fetch interval
			await FirebaseRemoteConfig.fetchConfig({
				minimumFetchIntervalInSeconds: 3600 // (3600 seconds) 1 hour in seconds
			});
			await FirebaseRemoteConfig.activate();

			this.initialized = true;
			console.log("VersionCheckService: initialized successfully");
		} catch (error) {
			console.error("VersionCheckService: Failed to initialize", error);
			throw error;
		}
	}

	/**
	 * Check if an update is available
	 * Returns version information and update requirements
	 */
	async checkForUpdate(): Promise<VersionCheckResult> {
		try {
			const deviceInfo = await Device.getInfo();

			// For web platform, return no update available
			if (deviceInfo.platform === "web") {
				return {
					updateAvailable: false,
					forceUpdate: false,
					currentVersion: packageInfo.version,
					latestVersion: packageInfo.version,
					updateMessage: "",
					storeUrl: ""
				};
			}

			// Ensure initialized
			if (!this.initialized) {
				await this.initialize();
			}

			const platform = deviceInfo.platform as "ios" | "android";
			const currentVersion = packageInfo.version;

			// Fetch latest config
			await FirebaseRemoteConfig.fetchAndActivate();

			// Get remote config values
			const minimumVersionConfig = await FirebaseRemoteConfig.getString({ key: "minimum_app_version" });
			const latestVersionConfig = await FirebaseRemoteConfig.getString({ key: "latest_app_version" });
			const forceUpdateConfig = await FirebaseRemoteConfig.getBoolean({ key: "force_update" });
			const updateMessageConfig = await FirebaseRemoteConfig.getString({ key: "update_message" });
			const storeUrlsConfig = await FirebaseRemoteConfig.getString({ key: "store_urls" });

			// Parse JSON values
			const minimumVersion = JSON.parse(minimumVersionConfig.value);
			const latestVersion = JSON.parse(latestVersionConfig.value);
			const storeUrls = JSON.parse(storeUrlsConfig.value);

			const minimumRequiredVersion = minimumVersion[platform];
			const availableVersion = latestVersion[platform];
			const forceUpdate = forceUpdateConfig.value;
			const updateMessage = updateMessageConfig.value;
			const storeUrl = storeUrls[platform];

			// Check if update is needed
			const updateAvailable = this.isVersionLower(currentVersion, availableVersion);
			const isBelowMinimum = this.isVersionLower(currentVersion, minimumRequiredVersion);

			console.log("VersionCheckService: Version check completed", {
				currentVersion,
				availableVersion,
				minimumRequiredVersion,
				updateAvailable,
				forceUpdate: forceUpdate || isBelowMinimum
			});

			return {
				updateAvailable,
				forceUpdate: forceUpdate || isBelowMinimum,
				currentVersion,
				latestVersion: availableVersion,
				updateMessage,
				storeUrl
			};
		} catch (error) {
			console.error("VersionCheckService: Failed to check for update", error);

			// Return safe default on error
			return {
				updateAvailable: false,
				forceUpdate: false,
				currentVersion: packageInfo.version,
				latestVersion: packageInfo.version,
				updateMessage: "",
				storeUrl: ""
			};
		}
	}

	/**
	 * Compare two semantic version strings
	 * Returns true if version1 is lower than version2
	 */
	private isVersionLower(version1: string, version2: string): boolean {
		const v1Parts = version1.split(".").map((v) => parseInt(v, 10));
		const v2Parts = version2.split(".").map((v) => parseInt(v, 10));

		for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
			const v1 = v1Parts[i] || 0;
			const v2 = v2Parts[i] || 0;

			if (v1 < v2) return true;
			if (v1 > v2) return false;
		}

		return false;
	}

	/**
	 * Get all version configuration from Remote Config
	 */
	async getVersionConfig(): Promise<VersionConfig | null> {
		try {
			if (!this.initialized) {
				await this.initialize();
			}

			await FirebaseRemoteConfig.fetchAndActivate();

			const minimumVersion = await FirebaseRemoteConfig.getString({ key: "minimum_app_version" });
			const latestVersion = await FirebaseRemoteConfig.getString({ key: "latest_app_version" });
			const forceUpdate = await FirebaseRemoteConfig.getBoolean({ key: "force_update" });
			const updateMessage = await FirebaseRemoteConfig.getString({ key: "update_message" });
			const storeUrls = await FirebaseRemoteConfig.getString({ key: "store_urls" });

			return {
				minimumVersion: JSON.parse(minimumVersion.value),
				latestVersion: JSON.parse(latestVersion.value),
				forceUpdate: forceUpdate.value,
				updateMessage: updateMessage.value,
				storeUrls: JSON.parse(storeUrls.value)
			};
		} catch (error) {
			console.error("VersionCheckService: Failed to get version config", error);
			return null;
		}
	}
}
